import React, { useMemo } from "react"

import { Check, ChevronsUpDown, Loader2 } from "lucide-react"

import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { Button } from "@/components/ui/button"
import type { DrbOption } from "@/hooks/useCollections"
import { cn } from "@/lib/utils"

export type AssignmentPicker = "vehicle" | "trailer" | "driver" | "second_driver"

interface AssignmentComboboxProps {
  options: DrbOption[]
  value: string | null
  displayValue: string | null
  onSelect: (id: string | null, name: string | null) => void
  placeholder: string
  searchPlaceholder: string
  isLoading?: boolean
  isSaving?: boolean
  // Open state is lifted: only one list may be open, and the backdrop must know before deciding
  // what a click closes.
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Merged with tailwind-merge, so a passed height replaces the default. */
  triggerClassName?: string
}

export const AssignmentCombobox: React.FC<AssignmentComboboxProps> = ({
  options,
  value,
  displayValue,
  onSelect,
  placeholder,
  searchPlaceholder,
  isLoading,
  isSaving,
  open,
  onOpenChange,
  triggerClassName,
}) => {
  // Falls back to the displayed name: `value` is null when the DRB record has no link for a
  // route-sourced assignment.
  const selectedId = useMemo(() => {
    if (value && options.some((option) => option.id === value)) return value
    const name = displayValue?.trim().toLowerCase()
    if (!name) return null
    return options.find((option) => option.name.trim().toLowerCase() === name)?.id ?? null
  }, [value, displayValue, options])

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={isLoading || isSaving}
          // role="combobox" can't take its name from its contents, and an unassigned field has no
          // title.
          aria-label={displayValue ?? placeholder}
          title={displayValue ?? undefined}
          // rounded-md, not the Button's rounded-full: a pill reads as an action, not a select.
          className={cn(
            "h-7 w-full justify-between rounded-md border-palette-jungleGreen300 bg-white px-2 text-sm font-medium text-palette-jungleGreen900 hover:bg-palette-jungleGreen000",
            triggerClassName
          )}
        >
          {isLoading ? (
            <span className="flex items-center gap-1.5 text-palette-jungleGreen400">
              <Loader2 size={12} className="animate-spin" />
              Loading...
            </span>
          ) : isSaving ? (
            <span className="flex items-center gap-1.5 text-amber-600">
              <Loader2 size={12} className="animate-spin" />
              Saving...
            </span>
          ) : displayValue ? (
            <span className="truncate">{displayValue}</span>
          ) : (
            <span className="text-palette-jungleGreen400">{placeholder}</span>
          )}
          <ChevronsUpDown className="ml-1 h-3.5 w-3.5 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      {/* Matches the trigger width; divided by the app zoom because content is zoomed (see
          index.css). */}
      <PopoverContent
        variant="plain"
        className="w-[calc(var(--radix-popover-trigger-width)/var(--app-zoom))] min-w-[260px]"
        align="start"
      >
        <Command>
          <CommandInput placeholder={searchPlaceholder} className="h-9" />
          <CommandList>
            <CommandEmpty>No results found.</CommandEmpty>
            <CommandGroup>
              <CommandItem
                value="__unassign__"
                // `aria-selected` is cmdk's keyboard highlight, not the assignment.
                data-current={!displayValue}
                onSelect={() => {
                  onSelect(null, null)
                  onOpenChange(false)
                }}
              >
                <span className="italic text-palette-jungleGreen400">Unassigned</span>
                {!displayValue && <Check className="ml-auto h-4 w-4 text-amber-500" />}
              </CommandItem>
              {options.map((option) => (
                <CommandItem
                  key={option.id}
                  value={option.name}
                  data-current={selectedId === option.id}
                  onSelect={() => {
                    onSelect(option.id, option.name)
                    onOpenChange(false)
                  }}
                >
                  <span className="truncate">{option.name}</span>
                  {selectedId === option.id && (
                    <Check className="ml-auto h-4 w-4 flex-none text-amber-500" />
                  )}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
